import { Injectable, NgModuleRef, OnDestroy } from '@angular/core';
import {
  AppContainerOptions,
  AppContainerService,
  AppLeftPaneService,
  AppRightPaneService,
  ContentSideNavButton,
  LinkSideNavButton,
  RightDrawerContent,
  SideNavBarService,
  SideNavButton,
} from '@mydocma/app-container';
import { MmProjectService } from '../mm-project/mm-project.service';
import { Project as MmProject } from '@docma-mm-core/project/project';
import { MmModule } from '../mm.module';
import { MmRoutingService } from '../mm-routing/mm-routing.service';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { ActivatedRoute, Router } from '@angular/router';
import { MmSideNavBarButtonsEnum } from './mm-sidenavbar-buttons.enum';
import { MmProjectPermissionsService } from '../mm-project/mm-project-permissions-service';
import { ProjectUserPermissions } from '@docma-mm-core/permission/project-user-permissions';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, combineLatest } from 'rxjs';
import { debounceTime, filter, first, switchMap, tap } from 'rxjs/operators';
import { MmTaskDashboardComponent } from '../mm-components/app-container/mm-task-dashboard/mm-task-dashboard.component';
import { UserSettingsMenuItem } from '@mydocma/app-container/lib/user-menu/user-menu-items/user-settings/user-settings-menu-item';
import { buttonsMap, idToTranslationKey, idToUrlMap, leftPaneContentMap, urlToButtonMap } from './mm-sidnavbar-buttons.maps';
import { DeviceService } from '@mydocma/core/src/lib/device';
import { MmFaultRightPaneService } from '../mm-faults/mm-faults-view/mm-fault-right-pane/mm-fault-right-pane.service';
import { MmLayoutStateService } from '../mm-filter/layout/layout-state.service';



@UntilDestroy()
@Injectable()
export class MmSideNavBarService implements OnDestroy {


  private buttons: SideNavButton[] = [];
  private currentProject: MmProject;
  private fistProjectChanged = new BehaviorSubject<boolean>(false);
  private currentRoute: string;
  private appContainerOptions: AppContainerOptions;

  isleftPaneOpened: boolean = false;
  private userPermissions: ProjectUserPermissions;

  private leftPaneContentMap = leftPaneContentMap;
  private buttonsMap = buttonsMap
  private urlToButtonMap = urlToButtonMap;
  private idToUrlMap = idToUrlMap;
  private idToTranslationKey = idToTranslationKey;


  private lastOpenedContentButton: ContentSideNavButton;
  private contentClosedFromCode = false;

  private isSideNavOpen = false;
  private isRightPaneOpen = false;
  private isLeftPaneOpen = false;

  constructor(private readonly projectService: MmProjectService,
    private readonly translateService: TranslateService,
    private readonly appContainerService: AppContainerService,
    private readonly leftPaneService: AppLeftPaneService,
    private readonly sideNavBarService: SideNavBarService,
    private readonly permissionsService: MmProjectPermissionsService,
    private readonly routingService: MmRoutingService,
    private readonly moduleRef: NgModuleRef<MmModule>,
    private readonly deviceService: DeviceService,
    private readonly faultRightPaneService: MmFaultRightPaneService,
    private readonly appRightPaneService: AppRightPaneService,
    private readonly layoutStateService: MmLayoutStateService) {


    this.leftPaneContentMap.forEach(e => e.moduleRef = this.moduleRef);
    this.initButtons();

    this.sideNavBarService.connected
      .pipe(
        switchMap(c => this.appContainerService.mobileView$),
        switchMap(ex => this.permissionsService.permissions$.pipe(tap(ps => this.userPermissions = ps))),
        switchMap(pr => this.routingService.moduleActivatedRoute$.pipe(tap(ar => this.onRouteChanged(ar)))),
        switchMap(r => this.sideNavBarService.expanded$),
        untilDestroyed(this))
      .subscribe(value => {
        try {
          this.refreshButtons();
        } catch (ex) {

        }

      })




    this.leftPaneService.connected$
      .pipe(
        filter(Boolean),
        switchMap(c => this.fistProjectChanged.pipe(filter(Boolean))),
        switchMap(pr => this.routingService.moduleActivatedRoute$
          .pipe(
            filter(ar => !!(ar && this.routingService.getRoutePath(ar.snapshot))),
            first()
            )),
        untilDestroyed(this)
      )
      .subscribe(f => this.onFirstProjectChanged());


    this.projectService.currentProject
      .pipe(untilDestroyed(this))
      .subscribe(p => this.onProjectChanged(p));

    this.appContainerService.isLeftPaneOpen
      .pipe(untilDestroyed(this))
      .subscribe(isOpen => {
        this.isleftPaneOpened = isOpen;
      });

    this.appContainerService.activeContentButton
      .pipe(untilDestroyed(this))
      .subscribe(async btn => {
        if (!this.contentClosedFromCode) {
          this.lastOpenedContentButton = btn;
        }
        if (btn) {
          const btnInfo = this.urlToButtonMap[this.currentRoute];
          switch (btnInfo?.group) {
            case MmSideNavBarButtonsEnum.ProjectSettingsGroup:
            case MmSideNavBarButtonsEnum.UserSettingsGroup:
              this.routingService.navigate([this.idToUrlMap.get(MmSideNavBarButtonsEnum.Faults)]);
              break;
          }
        }
      });

    this.appContainerService.notifyChanges(appcontainer => {
      const userMenuItem = appcontainer.header.userMenuItems.items.find(x => x.id == 'userSettings') as UserSettingsMenuItem;
      userMenuItem.click = async () => {
        const url = this.idToUrlMap.get(MmSideNavBarButtonsEnum.UserSettingsVariables);
        if (url) {
          this.routingService.navigate([url]);
        }
      };
    });

    this.translateService.onLangChange
      .pipe(untilDestroyed(this))
      .subscribe(l => this.transalteButtons());
  }

  private getButtonsByGroupId(groupId: MmSideNavBarButtonsEnum) {
    return Object.values(this.buttonsMap).filter(x => x.group === groupId);
  }


  private onRouteChanged(ar: ActivatedRoute) {
    this.currentRoute = ar && this.routingService.getRoutePath(ar.snapshot)

    const btnInfo = this.urlToButtonMap[this.currentRoute];

    if (btnInfo) {
      switch (btnInfo.group) {
        case MmSideNavBarButtonsEnum.ProjectSettingsGroup:
        case MmSideNavBarButtonsEnum.UserSettingsGroup:
          this.closeContentSideNavFormCode();
          break;
        case MmSideNavBarButtonsEnum.MainGroup:

          if (this.lastOpenedContentButton && !this.isleftPaneOpened) {
            this.appContainerService.openContentSideNav(this.lastOpenedContentButton.id, this.lastOpenedContentButton.content);
          }
          break;
      }
    }

    this.setActiveLinkButton(this.currentRoute, ar?.snapshot?.queryParams)
  }

  private closeContentSideNavFormCode() {
    this.contentClosedFromCode = true;
    this.appContainerService.closeContentSideNav();
    this.contentClosedFromCode = false;
  }

  private async refreshButtons() {
    const btnInfo = this.urlToButtonMap[this.currentRoute];
    const isBarOpened = this.sideNavBarService.isBarOpened();
    const isMobil = this.appContainerService.isMobileView;
    let hasSomeProjectSettings = false;

    if (btnInfo) {
      if (btnInfo.group === MmSideNavBarButtonsEnum.UserSettingsGroup) {
        // refresh  user settings sidenavbar btns
        this.getButtonsByGroupId(MmSideNavBarButtonsEnum.FilterGroup).forEach(btn => {
          btn.button.hidden = true;
        });
        this.getButtonsByGroupId(MmSideNavBarButtonsEnum.MainGroup).forEach(btn => {
          btn.button.hidden = true;
        });
        this.getButtonsByGroupId(MmSideNavBarButtonsEnum.ProjectSettingsGroup).forEach(btn => {
          btn.button.hidden = true;
        });

        this.getButtonsByGroupId(MmSideNavBarButtonsEnum.UserSettingsGroup).forEach(btn => {
          btn.button.hidden = btn.button.id === MmSideNavBarButtonsEnum.UserSettingsGroup ? !isBarOpened : false;
          if (btn.button.id !== MmSideNavBarButtonsEnum.UserSettingsBackToProject &&
            btn.button.id != MmSideNavBarButtonsEnum.UserSettingsGroup) {
            if (!isBarOpened) {
              btn.button.parentId = undefined;
            }
            else {
              btn.button.parentId = MmSideNavBarButtonsEnum.UserSettingsGroup;
            }
            btn.button.hidden = false;
            btn.hasPermission = true;
          }

        });

      } else if (btnInfo.group === MmSideNavBarButtonsEnum.ProjectSettingsGroup) {

        this.getButtonsByGroupId(MmSideNavBarButtonsEnum.FilterGroup).forEach(btn => {
          btn.button.hidden = !isBarOpened;
        });
        this.getButtonsByGroupId(MmSideNavBarButtonsEnum.MainGroup).forEach(btn => {
          btn.button.hidden = !isBarOpened
        });

        this.getButtonsByGroupId(MmSideNavBarButtonsEnum.UserSettingsGroup).forEach(btn => {
          btn.button.hidden = true;
        });


        this.getButtonsByGroupId(MmSideNavBarButtonsEnum.ProjectSettingsGroup).forEach(btn => {
          switch (btn.button.id) {
            case MmSideNavBarButtonsEnum.ProjectSettingsBackToMain:
              btn.button.hidden = isBarOpened || isMobil
              btn.button.parentId = undefined;
              break;
            case MmSideNavBarButtonsEnum.ProjectSettingsGroup:
              btn.button.hidden = !isBarOpened || isMobil;
              break
            default:
              const perm = this.hasConfigPermission(this.userPermissions, btn.button);
              btn.button.parentId = isBarOpened ? MmSideNavBarButtonsEnum.ProjectSettingsGroup : undefined;
              btn.button.hidden = (isMobil || !perm);
              btn.hasPermission = perm;
              hasSomeProjectSettings = perm || hasSomeProjectSettings;
          }
        });


      } else if (btnInfo.group === MmSideNavBarButtonsEnum.MainGroup) {


        this.getButtonsByGroupId(MmSideNavBarButtonsEnum.FilterGroup).forEach(btn => {
          btn.button.hidden = false;
        });
        this.getButtonsByGroupId(MmSideNavBarButtonsEnum.MainGroup).forEach(btn => {
          btn.button.hidden = false;
        });

        this.getButtonsByGroupId(MmSideNavBarButtonsEnum.UserSettingsGroup).forEach(btn => {
          btn.button.hidden = true;
        });

        this.getButtonsByGroupId(MmSideNavBarButtonsEnum.ProjectSettingsGroup).forEach(btn => {
          switch (btn.button.id) {
            case MmSideNavBarButtonsEnum.ProjectSettingsBackToMain:
              btn.button.hidden = true
              break;
            case MmSideNavBarButtonsEnum.ProjectSettingsGroup:
              btn.button.hidden =  isMobil
              break;
            default:
              const perm = this.hasConfigPermission(this.userPermissions, btn.button);
              btn.button.parentId = isBarOpened ? MmSideNavBarButtonsEnum.ProjectSettingsGroup : undefined;
              btn.button.hidden = (!isBarOpened || isMobil || !perm);
              btn.hasPermission = perm;
              hasSomeProjectSettings = perm || hasSomeProjectSettings;
          }
        });
      }


      const plansBtn = this.buttonsMap[MmSideNavBarButtonsEnum.Plans]
      if (!plansBtn.button.hidden) {
        plansBtn.button.hidden = !isMobil;
      }

      const settingsGroupBtn = this.buttonsMap[MmSideNavBarButtonsEnum.ProjectSettingsGroup]
      if (!settingsGroupBtn.button.hidden) {
        settingsGroupBtn.button.hidden = !hasSomeProjectSettings;
      }


      this.sideNavBarService.refreshNavbar();

    }
    else if (this.currentRoute) {
      console.error('No button found for url' + this.currentRoute)
    }
  }

  ngOnDestroy(): void {
  }

  private setActiveLinkButton(path: string, queryParams?: { [key: string]: any }) {
    if (!path) {
      return;
    }

    let pathInternal = path;

    let activeBtn = (this.urlToButtonMap.hasOwnProperty(pathInternal) ? this.urlToButtonMap[pathInternal] : null);
    if (!activeBtn) {
      return;
    }
    if (this.appContainerService.isMobileView) {
      if (activeBtn.content === MmSideNavBarButtonsEnum.Faults && queryParams?.view === 'plan') {
        activeBtn = { content: MmSideNavBarButtonsEnum.Plans, group: MmSideNavBarButtonsEnum.MainGroup }
      }
    }
    this.buttons.forEach(b => {
      if (b instanceof LinkSideNavButton && activeBtn.content === b.id) {
        b.active = true;
        // console.warn(`(6) SET ${(b.active ? 'ACTIVE' : 'INACTIVE')}: ${b.id}`);
      } else {
        b.active = false;
      }
    });
  }

  private transalteButtons() {
    this.idToTranslationKey.forEach((key, id) => {
      const btn = this.buttonsMap[id];
      if (btn) {
        btn.button.text = this.translateService.instant(key);
        if(this.deviceService.isDesktop()) {
          btn.button.tooltip = this.translateService.instant(key);
        }
      }
    })
  }


  private onProjectChanged(p: MmProject) {
    this.currentProject = p;
    if (this.currentProject && !this.fistProjectChanged.value) {
      setTimeout(() => {
        this.fistProjectChanged.next(true);
      }, 300);
    }
    if(this.deviceService.isMobil() && !this.deviceService.isTablet()) {
      this.appContainerService.closeContentSideNav();
      this.sideNavBarService.collapseBar();
      this.sideNavBarService.openBar();
    }
    if(this.deviceService.isTablet()) {
      this.onFirstProjectChanged();
      this.sideNavBarService.collapseBar();
      this.sideNavBarService.openBar();
    }
  }

  private onFirstProjectChanged() {
    // Show structure after first project selected
    const btnInfo = this.urlToButtonMap[this.currentRoute];
    if (!this.deviceService.isMobil()  && btnInfo?.group === MmSideNavBarButtonsEnum.MainGroup) {
      setTimeout(() => {
        if(!this.layoutStateService.defaultLayoutHasNoFields) {
          const btn = this.buttonsMap[MmSideNavBarButtonsEnum.FilterStructure].button as ContentSideNavButton;
          this.appContainerService.openContentSideNav(btn.id, btn.content);
        }
      }, 100);
    }

  }

  private hasConfigPermission(ps: ProjectUserPermissions, button: SideNavButton) {

    if (!ps?.Configs) {
      return false;
    }
    return this.permissionsService.hasRoutePermissions(ps, this.idToUrlMap.get(button.id))
  }

  register(appContainer: AppContainerOptions) {
    this.appContainerOptions = appContainer;
    this.leftPaneService.setLeftPaneFlexBasisPx(400);
    appContainer.sideNavBar.buttons = this.buttons;
    this.appContainerOptions.rightDrawer.contents = [
      new RightDrawerContent({
        componentType: MmTaskDashboardComponent,
        id: 'task-dashboard',
        moduleRef: this.moduleRef
      }),
    ];

  }

  unregister(appContainer: AppContainerOptions) {
    appContainer.sideNavBar.buttons = [];
    this.appContainerOptions = null;
  }

  private async onLinkButtonClick(btnId: string) {
    const url = this.idToUrlMap.get(btnId);

    switch (btnId) {
      case MmSideNavBarButtonsEnum.Plans:
        this.routingService.navigate([url], { queryParams: { view: 'plan' } });
        break;
      case MmSideNavBarButtonsEnum.ProjectSettingsBackToMain:
        this.routingService.navigate([this.idToUrlMap.get(MmSideNavBarButtonsEnum.Faults)]);
        break
      case MmSideNavBarButtonsEnum.ProjectSettingsGroup:
        if (!this.sideNavBarService.isBarOpened()) {
          const firstProjectSetting =
            this.getButtonsByGroupId(MmSideNavBarButtonsEnum.ProjectSettingsGroup)
              .find(x => x.button.id != MmSideNavBarButtonsEnum.ProjectSettingsBackToMain &&
                x.button.id != MmSideNavBarButtonsEnum.ProjectSettingsGroup &&
                x.hasPermission);
          if (firstProjectSetting) {
            this.routingService.navigate([this.idToUrlMap.get(firstProjectSetting.button.id)]);
          }
        }
        break
      default:
        if (url) {
          this.routingService.navigate([url]);
        }
    }

  }

  private initButtons() {
    this.buttonsMap[MmSideNavBarButtonsEnum.FilterGroup].button.click.subscribe(b => {
      // this.onFilterGroupClick();
      setTimeout(() => { this.sideNavBarService.refreshNavbar() }, 100);
    });

    this.transalteButtons();
    for (const btn of Object.values(this.buttonsMap)) {
      this.buttons.push(btn.button);

      if (btn.button instanceof LinkSideNavButton) {
        btn.button.click.subscribe(async b => await this.onLinkButtonClick(btn.button.id));
      }
    }
  }



}
